import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type CapabilityMode,
  type DelegationResponse,
  getDelegationMatrix,
  updateDelegationMatrix,
} from "@/api/admin-api"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

export const DELEGATION_QUERY_KEY = ["admin", "delegation"] as const

/**
 * The live delegation matrix — shared by the platform-admin "preview as
 * customer admin" toggle and every inline per-section delegation-mode
 * control (`delegation-mode-control.tsx`). `enabled` is normally
 * `is_platform_admin`: no point fetching this for anyone else, but every
 * platform-admin-gated consumer on the page shares this one cached query.
 */
export function useDelegationMatrixQuery(enabled: boolean) {
  return useQuery<DelegationResponse>({
    queryKey: DELEGATION_QUERY_KEY,
    queryFn: getDelegationMatrix,
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

/**
 * Sets a single capability's delegation mode. Sends only that one key —
 * the backend merges it onto the freshest stored matrix inside its own
 * CAS-retry closure (see `routes/admin/delegation.py::update_delegation`),
 * so this never needs (or risks) reading a possibly stale or not-yet-loaded
 * client-side cache to build a "full" payload. Two sections' controls fired
 * in quick succession each land as independent, safely-merged patches.
 */
export function useUpdateDelegationCapabilityMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ capability, mode }: { capability: string; mode: CapabilityMode }) =>
      await updateDelegationMatrix({ [capability]: mode }),
    onSuccess: (result) => {
      // Write the PUT's own (authoritative, server-merged) response straight
      // into the cache before invalidating. invalidateQueries only schedules
      // a background refetch — if that refetch itself fails (a network blip
      // right after a successful save), the cache would keep serving the
      // pre-update matrix despite the write having already succeeded and a
      // success toast already shown.
      queryClient.setQueryData<DelegationResponse>(DELEGATION_QUERY_KEY, (prev) =>
        prev
          ? {
              ...prev,
              capabilities: result.capabilities,
              registry: prev.registry.map((entry) => ({
                ...entry,
                mode: result.capabilities[entry.key] ?? "hidden",
              })),
            }
          : prev
      )
      void queryClient.invalidateQueries({ queryKey: DELEGATION_QUERY_KEY })
    },
  })
}
